'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { subscribeToTable } from '@/lib/supabase/realtime-registry';
import { api } from '@/lib/utils/api';
import { type CERConfig, DEFAULT_CER_CONFIG } from '@slims/shared/types';

type ScopedConfig = Omit<CERConfig, 'id' | 'tenantId'> & { id?: string; tenantId?: string };

interface UseScopedConfigReturn {
	/** Always a full config — seeded with DEFAULT_CER_CONFIG until the fetch resolves. */
	config: ScopedConfig;
	loading: boolean;
}

/** React Query key for a scoped CER configuration. Exported so mutations elsewhere can invalidate it. */
export const scopedConfigKey = (orgId: string | null | undefined) => ['scoped-config', orgId] as const;

/** Fetcher for a scoped CER config — shared by the hook and by hover-prefetch. */
export const fetchScopedConfig = (orgId: string) =>
	api.get<ScopedConfig>(`/api/cer?orgId=${orgId}`);

/**
 * Fetches a single CER configuration scoped to `orgId` without touching the
 * global cer-store, so multiple scoped panels can be rendered side by side.
 *
 * ADR-006: backed by React Query — the result is cached and deduped across the
 * N panels of a «Panoramica» and across navigations. The `organizations`
 * realtime channel invalidates the entry so a rename/GSE edit from another panel
 * or tab refreshes it.
 */
export function useScopedConfig(orgId: string | null | undefined): UseScopedConfigReturn {
	const queryClient = useQueryClient();

	const { data, isLoading } = useQuery({
		queryKey: scopedConfigKey(orgId),
		enabled: !!orgId,
		queryFn: () => fetchScopedConfig(orgId as string),
	});

	useEffect(() => {
		if (!orgId) return;
		return subscribeToTable(
			`organizations-scoped-config-${orgId}`,
			{ event: 'UPDATE', schema: 'public', table: 'organizations', filter: `id=eq.${orgId}` },
			() => {
				queryClient.invalidateQueries({ queryKey: scopedConfigKey(orgId) });
			},
		);
	}, [orgId, queryClient]);

	return { config: data ?? DEFAULT_CER_CONFIG, loading: orgId ? isLoading : true };
}
